import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CheckCircle, XCircle, Clock, User, Phone, Mail, CalendarDays, ArrowLeft, DollarSign, MessageSquare } from "lucide-react";
import { useAuthState } from "@/hooks/useAuthState";
import { useToast } from "@/hooks/use-toast";
import { BookingService, BookingRequestWithDetails } from "@/services/bookingService";
import ApproveBookingModal from "@/components/ApproveBookingModal";

const OwnerBookings = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { isAuthenticated, userType, userName, userPhoto, userId, loading: authLoading } = useAuthState();
  const [bookingRequests, setBookingRequests] = useState<BookingRequestWithDetails[]>([]);
  const [loading, setLoading] = useState(false);
  const [approveModalOpen, setApproveModalOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState<BookingRequestWithDetails | null>(null);
  const [approving, setApproving] = useState(false);

  useEffect(() => {
    // Aguardar carregamento da autenticação antes de verificar
    if (!authLoading && (!isAuthenticated || userType !== "owner")) {
      navigate("/login?profile=owner");
    }
  }, [isAuthenticated, userType, authLoading, navigate]);

  useEffect(() => {
    if (isAuthenticated && userType === "owner" && userId) {
      loadBookingRequests();
    }
  }, [isAuthenticated, userType, userId]);

  const loadBookingRequests = async () => {
    if (!userId) return;
    
    try {
      setLoading(true);
      const requests = await BookingService.getBookingRequestsByOwner(userId);
      setBookingRequests(requests);
    } catch (error) {
      console.error("Error loading booking requests:", error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar as solicitações de reserva.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleApproveClick = (booking: BookingRequestWithDetails) => {
    setSelectedBooking(booking);
    setApproveModalOpen(true);
  };

  const handleApproveRequest = async (discountAmount: number, ownerComment: string) => {
    if (!selectedBooking) return;

    try {
      setApproving(true);
      await BookingService.approveBookingRequest(selectedBooking.id, discountAmount, ownerComment);
      
      const discountText = discountAmount > 0 ? ` com desconto de R$ ${discountAmount.toLocaleString('pt-BR')}` : '';
      toast({
        title: "Reserva aprovada!",
        description: `A reserva foi aprovada com sucesso${discountText}.`,
      });
      
      setApproveModalOpen(false);
      setSelectedBooking(null);
      loadBookingRequests();
    } catch (error) {
      console.error("Error approving request:", error);
      toast({
        title: "Erro",
        description: "Não foi possível aprovar a reserva.",
        variant: "destructive",
      });
    } finally {
      setApproving(false);
    }
  };

  const handleRejectRequest = async (requestId: number) => {
    const confirmed = window.confirm('Tem certeza que deseja rejeitar esta reserva? Esta ação não pode ser desfeita.');
    
    if (confirmed) {
      try {
        await BookingService.rejectBookingRequest(requestId);
        toast({
          title: "Reserva rejeitada",
          description: "A reserva foi rejeitada.",
        });
        loadBookingRequests();
      } catch (error) {
        console.error("Error rejecting request:", error);
        toast({
          title: "Erro",
          description: "Não foi possível rejeitar a reserva.",
          variant: "destructive",
        });
      }
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "pending":
        return <Badge variant="secondary" className="bg-yellow-100 text-yellow-800"><Clock className="h-3 w-3 mr-1" />Pendente</Badge>;
      case "approved":
        return <Badge variant="default" className="bg-green-100 text-green-800"><CheckCircle className="h-3 w-3 mr-1" />Aprovada</Badge>;
      case "rejected":
        return <Badge variant="destructive" className="bg-red-100 text-red-800"><XCircle className="h-3 w-3 mr-1" />Rejeitada</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  // Mostrar loading enquanto verifica autenticação
  if (authLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar isAuthenticated={false} />
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-muted-foreground">Verificando autenticação...</p>
    </div>
    </div>
    </div>
    </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar 
        isAuthenticated={isAuthenticated} 
        isOwner={true} 
        userName={userName}
        userPhoto={userPhoto}
        userId={userId}
        userType={userType}
      />

      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/")}
              className="gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Voltar
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">
                Solicitações de Reservas
              </h1>
              <p className="text-muted-foreground">
                Gerencie as solicitações de reserva dos seus imóveis
              </p>
    </div>
    </div>
    </div>

        {/* Estatísticas */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total de Solicitações</CardTitle>
              <CalendarDays className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{bookingRequests.length}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Pendentes</CardTitle>
              <Clock className="h-4 w-4 text-yellow-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-yellow-600">
                {bookingRequests.filter(r => r.status === "pending").length}
    </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Aprovadas</CardTitle>
              <CheckCircle className="h-4 w-4 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">
                {bookingRequests.filter(r => r.status === "approved").length}
    </div>
            </CardContent>
          </Card>
    </div>

        {/* Lista de Solicitações */}
        <div className="space-y-6">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
    </div>
          ) : bookingRequests.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-8">
                <CalendarDays className="h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">Nenhuma solicitação encontrada</h3>
                <p className="text-muted-foreground text-center">
                  Você ainda não recebeu solicitações de reserva para seus imóveis.
                </p>
              </CardContent>
            </Card>
          ) : (
            bookingRequests.map((request) => (
              <Card key={request.id} className="overflow-hidden">
                <CardHeader className="bg-muted/50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                        <User className="h-6 w-6 text-primary" />
    </div>
                      <div>
                        <h3 className="font-semibold text-lg">{request.property_title}</h3>
                        <p className="text-sm text-muted-foreground">
                          Solicitação #{request.id} • {new Date(request.created_at).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' })}
                        </p>
    </div>
    </div>
                    {getStatusBadge(request.status)}
    </div>
                </CardHeader>
                <CardContent className="pt-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="font-medium mb-3 flex items-center gap-2">
                        <User className="h-4 w-4" />
                        Dados do Solicitante
                      </h4>
                      <div className="space-y-2 text-sm">
                        <p><strong>Nome:</strong> {request.guest_name}</p>
                        <p className="flex items-center gap-1">
                          <Mail className="h-3 w-3" />
                          {request.guest_email}
                        </p>
                        <p className="flex items-center gap-1">
                          <Phone className="h-3 w-3" />
                          {request.guest_phone}
                        </p>
    </div>
    </div>

                    <div>
                      <h4 className="font-medium mb-3">Detalhes da Reserva</h4>
                      <div className="space-y-2 text-sm">
                        <p><strong>Check-in:</strong> {new Date(request.check_in).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' })}</p>
                        <p><strong>Check-out:</strong> {new Date(request.check_out).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' })}</p>
                        <p><strong>Noites:</strong> {request.total_nights}</p>
                        <p><strong>Valor Total:</strong> R$ {request.total_price.toLocaleString('pt-BR')}</p>
                        {request.discount_amount && request.discount_amount > 0 && (
                          <>
                            <p className="flex items-center gap-1 text-red-600">
                              <DollarSign className="h-3 w-3" />
                              <strong>Desconto:</strong> -R$ {request.discount_amount.toLocaleString('pt-BR')}
                            </p>
                            <p className="text-green-600 font-semibold">
                              <strong>Valor Final:</strong> R$ {(request.final_price || request.total_price - request.discount_amount).toLocaleString('pt-BR')}
                            </p>
                          </>
                        )}
    </div>
    </div>
    </div>

                  {request.guest_notes && (
                    <div className="mt-4 p-4 bg-muted/50 rounded-lg">
                      <h4 className="font-medium mb-2">Observações do Hóspede</h4>
                      <p className="text-sm text-muted-foreground">{request.guest_notes}</p>
    </div>
                  )}

                  {request.owner_comment && (
                    <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200 dark:border-blue-800">
                      <h4 className="font-medium mb-2 flex items-center gap-2 text-blue-700 dark:text-blue-300">
                        <MessageSquare className="h-4 w-4" />
                        Comentário do Proprietário
                      </h4>
                      <p className="text-sm text-blue-900 dark:text-blue-100">{request.owner_comment}</p>
    </div>
                  )}

                  {request.status === "pending" && (
                    <div className="flex gap-3 mt-6">
                      <Button
                        onClick={() => handleApproveClick(request)}
                        className="gap-2 bg-green-600 hover:bg-green-700"
                      >
                        <CheckCircle className="h-4 w-4" />
                        Aprovar
                      </Button>
                      <Button
                        variant="destructive"
                        onClick={() => handleRejectRequest(request.id)}
                        className="gap-2"
                      >
                        <XCircle className="h-4 w-4" />
                        Rejeitar
                      </Button>
    </div>
                  )}
                </CardContent>
              </Card>
            ))
          )}
    </div>
    </div>

      {/* Modal de Aprovação */}
      <ApproveBookingModal
        isOpen={approveModalOpen && !!selectedBooking}
        onClose={() => {
          setApproveModalOpen(false);
          setSelectedBooking(null);
        }}
        onApprove={handleApproveRequest}
        booking={selectedBooking || {
          id: 0,
          property_title: '',
          total_price: 0,
          total_nights: 0,
          check_in: '',
          check_out: ''
        }}
        loading={approving}
      />
    </div>
  );
};

export default OwnerBookings;
